import { displayVersionLabel } from './stepflowVersionLabel';

/**
 * Pure predicates + copy helpers for the Workbench "release" banner.
 *
 * Extracted from NetworkWorkbench.jsx so the visibility predicate can be
 * regression-locked at the unit level (the banner appeared / disappeared
 * unexpectedly three times in the last couple sessions — this file is the
 * invariant).
 *
 * State matrix for the banner:
 *
 *   Status    | pendingReleaseChanges | Banner | Copy
 *   ----------|-----------------------|--------|------------------------------
 *   DRAFT     | —                     | hide   | (network still in approval)
 *   ACTIVE    | —                     | show   | FIRST_RELEASE — "first release" CTA
 *   RELEASED  | false                 | show   | NEW_POOL — "Release to a new pool"
 *   RELEASED  | true                  | show   | RE_RELEASE — "changes since vN"
 *
 * DRAFT is never a runtime state the creator should see a release CTA in —
 * deploying (DRAFT → ACTIVE) is a Build-tab concern, release is post-deploy.
 *
 * The previous matrix hid the CTA on a clean RELEASED row, but that left no
 * affordance to release the same network to *another* customer pool (a
 * 1st-class workflow — many enterprise customers per network). The button
 * now stays visible while the network is deployed; "Release to a new pool"
 * is the only mode offered there (patch needs a pending change to make
 * sense).
 *
 * Consumers:
 *   - pages/NetworkWorkbench.jsx (workbench header banner)
 *   - tests/features/ui-workbench/unit/releaseBanner.test.ts (regression lock)
 */

/**
 * Lower-cased status for defensive comparison — DynamoDB has historically
 * stored this field in multiple shapes ('ACTIVE' / 'active' / NetworkStatus
 * enum), and the frontend has to handle all of them.
 *
 * @param {string | null | undefined} status
 * @returns {string}
 */
function normalizeStatus(status) {
  return String(status ?? '').toUpperCase();
}

/**
 * Is the banner's "first release" CTA appropriate?
 * True when the network is deployed (ACTIVE) but has never been released.
 *
 * @param {{ status?: string | null }} network
 * @returns {boolean}
 */
export function isFirstReleaseAvailable(network) {
  if (!network || typeof network !== 'object') return false;
  return normalizeStatus(network.status) === 'ACTIVE';
}

/**
 * Is the banner's "changes since last release" CTA appropriate?
 * True when the network is RELEASED AND the backend flipped
 * ``pendingReleaseChanges`` after a behavior-affecting edit (agent config or
 * HIL mode). The backend guarantees the flag is a boolean on every row
 * (legacy rows serialize as ``false``), so no truthy-undefined surprises.
 *
 * @param {{ status?: string | null, pendingReleaseChanges?: boolean }} network
 * @returns {boolean}
 */
export function isReReleaseAvailable(network) {
  if (!network || typeof network !== 'object') return false;
  if (normalizeStatus(network.status) !== 'RELEASED') return false;
  return network.pendingReleaseChanges === true;
}

/**
 * Is the "release to another pool" CTA appropriate?
 *
 * True when the network is RELEASED and clean — there are no behavior
 * changes pending, but the operator can still spin up another customer
 * pool from this same release. The dialog will open in NEW_POOL mode.
 *
 * @param {{ status?: string | null, pendingReleaseChanges?: boolean }} network
 * @returns {boolean}
 */
export function isNewPoolReleaseAvailable(network) {
  if (!network || typeof network !== 'object') return false;
  if (normalizeStatus(network.status) !== 'RELEASED') return false;
  return network.pendingReleaseChanges !== true;
}

/**
 * Single entry point for the banner: should we render it at all?
 *
 * @param {{ status?: string | null, pendingReleaseChanges?: boolean }} network
 * @returns {boolean}
 */
export function shouldShowReleaseBanner(network) {
  return (
    isFirstReleaseAvailable(network)
    || isReReleaseAvailable(network)
    || isNewPoolReleaseAvailable(network)
  );
}

/**
 * Banner copy + CTA label for the current mode. Returns ``null`` when the
 * banner should not be shown — callers can early-return on the null.
 *
 * @param {{ status?: string | null, pendingReleaseChanges?: boolean, releaseVersion?: number | null }} network
 * @param {{ releases?: Array }} options — optional releases list for proper MAJOR.MINOR label
 * @returns {{ mode: 'FIRST_RELEASE' | 'RE_RELEASE' | 'NEW_POOL', message: string, ctaLabel: string } | null}
 */
export function getReleaseBannerConfig(network, { releases } = {}) {
  if (isReReleaseAvailable(network)) {
    const v = Number.isFinite(network.releaseVersion) ? network.releaseVersion : null;
    // MAJOR.MINOR label when resolvable; never the retired ``vN``. If we can't
    // resolve a semantic label, omit the version suffix entirely.
    const versionLabel = v ? displayVersionLabel(v, releases) : '';
    const versionSuffix = versionLabel ? ` since Release ${versionLabel}` : '';
    return {
      mode: 'RE_RELEASE',
      message: `This network has changes${versionSuffix}. Release a new versioned snapshot to expose them in Agentic Networks.`,
      ctaLabel: 'Release a new Version',
    };
  }
  if (isFirstReleaseAvailable(network)) {
    return {
      mode: 'FIRST_RELEASE',
      message: 'This network is deployed. Release a versioned snapshot to expose it in Agentic Networks.',
      ctaLabel: 'Create a Room',
    };
  }
  if (isNewPoolReleaseAvailable(network)) {
    return {
      mode: 'NEW_POOL',
      message:
        'This network is released. Open a new customer pool from the current snapshot at any time.',
      ctaLabel: 'Create a Room',
    };
  }
  return null;
}
